import { wantedAnchors } from './grading.js';

/**
 * What a room still needs, spot by spot, in the order it is set: rounds, then
 * chairs, then linen.
 *
 * The counters on screen are totals, and totals hide the one thing a new
 * starter cannot find: a round with nine chairs and the next with seven reads
 * "48 of 48", a round set a ring off the plan reads "6 of 6 rounds" with one
 * ring empty, and the room never finishes with nothing on screen to say why.
 * This reads it the way the grade does, one planned spot at a time.
 *
 *   offPlan     tables standing anywhere the plan does not want one
 *   missing     planned spots with no round
 *   fewChairs   planned rounds short of their chairs
 *   bare        planned rounds with no cloth
 *   wrongCloth  planned rounds dressed in a colour the sheet does not want
 *   stage       'round', 'chair' or 'linen': the first of those still owed
 *               that can still be had (`gone` is the kinds that cannot today:
 *               a room short of chairs nobody will cover goes on to its linen)
 *   short       the kinds still owed that cannot be had
 */
export function roomNeeds(setup, spec, gone = new Set()) {
  if (!spec?.layout) return null;
  const planned = wantedAnchors(spec);
  const keys = [...planned];
  const at = (key) => setup.tableAt(key);
  const offPlan = setup.tables.filter((table) => !planned.has(table.key)).map((table) => table.key);
  const missing = keys.filter((key) => !at(key));
  const fewChairs = keys.filter((key) => at(key) && at(key).seats < spec.chairsPerTable);
  const bare = keys.filter((key) => at(key) && !at(key).cover);
  const wrongCloth = spec.linen ? keys.filter((key) => at(key)?.cover && at(key).cover.variant !== spec.linen) : [];
  const owed = { round: missing.length > 0, chair: fewChairs.length > 0, linen: bare.length + wrongCloth.length > 0 };
  const stage = ['round', 'chair', 'linen'].find((kind) => owed[kind] && !gone.has(kind)) ?? null;
  const short = ['round', 'chair', 'linen'].filter((kind) => owed[kind] && gone.has(kind));
  return { offPlan, missing, fewChairs, bare, wrongCloth, stage, short };
}
